import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Package } from 'lucide-react';
import api from '../utils/api';

function OrdersPage() {
  const { user, isLoaded } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for cart context to load before checking user
    if (!isLoaded) return;
    
    if (!user) {
      navigate('/');
      return;
    }

    const fetchOrders = async () => {
      try {
        const data = await api.getMyOrders(user.phone);
        setOrders(data.orders || []);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user, navigate, isLoaded]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <Skeleton className="h-10 w-48 mb-8" />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Package className="w-20 h-20 mx-auto text-muted-foreground" />
          <h2 className="text-2xl font-bold">No orders yet</h2>
          <p className="text-muted-foreground">Start shopping to see your orders here!</p>
          <Button onClick={() => navigate('/products')} size="lg" className="rounded-xl">
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="orders-page">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-3xl font-display font-normal mb-8">My Orders</h1>

        <div className="space-y-4">
          {orders.map(order => (
            <Card key={order.id} className="p-6" data-testid="order-card">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order #{order.id.slice(0, 8)}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(order.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge className={getStatusColor(order.payment_status)}>
                    {order.payment_status}
                  </Badge>
                  <Badge className={getStatusColor(order.order_status)}>
                    {order.order_status}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{item.title} x {item.quantity}</span>
                    <span className="font-medium">₹{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t flex justify-between items-center">
                <p className="font-semibold">Total: ₹{order.total_amount.toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">
                  {order.delivery_address?.city}, {order.delivery_address?.state}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OrdersPage;